// <img> for a stored photo. /api/photos/{ref} needs the bearer token, which a plain
// <img src> cannot send (the picture shows as a broken image) — so fetch the bytes with the
// token and hand the <img> an object URL. Results are cached per ref+width.
import { useEffect, useState } from "react";
import { API_BASE, getToken, photoRefPath } from "@/lib/api";

const cache = new Map();   // key -> object URL

export function photoPath(ref, w) {
  if (!ref) return null;
  return `${API_BASE}/photos/${photoRefPath(ref)}${w ? `?w=${w}` : ""}`;
}

export default function AuthImage({ refId, w, alt = "", className = "", fallback = null, ...rest }) {
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    if (!refId) { setSrc(null); return undefined; }
    if (/^(https?:|blob:|data:)/.test(refId)) { setSrc(refId); return undefined; }
    const key = `${refId}|${w || 0}`;
    if (cache.has(key)) { setSrc(cache.get(key)); return undefined; }
    setSrc(null);
    fetch(photoPath(refId, w), { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.blob(); })
      .then((b) => {
        const url = URL.createObjectURL(b);
        cache.set(key, url);
        if (!cancelled) setSrc(url);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [refId, w]);

  if (failed || !refId) return fallback;
  if (!src) return <div className={`${className} bg-neutral-100 animate-pulse`} aria-hidden="true" />;
  return <img src={src} alt={alt} className={className} {...rest} />;
}
